import React, { useEffect, useRef, useState } from "react";
import "./SelectUnselectExtended.css";
import Checkbox from "../Checkbox/Checkbox";
// import { usePopup } from "@/contexts/PopupContext";

export interface SelectUnselectExtendedProps {
  name: string;
  no3d?: boolean;
  imageUrl?: string;
  color?: string;
  price?: number;
  description?: string;
  Component?: React.FC;
  isSelected: boolean;
  onSelect: () => void;
  onUnselect: () => void;
  alwaysShowSelected?: boolean;
  selectedNames?: string;
  parentIsSelected?: boolean;
  carouselImages?: string[];
  stopPropagation?: boolean;
  disabled?: boolean;
  showComponent?: boolean;
  onShowComponentToggle?: (show: boolean) => void;
  scrollOnToggle?: boolean;
  showCheckbox?: boolean;
  deselectByCheckboxOnly?: boolean;
}

const SelectUnselectExtended: React.FC<SelectUnselectExtendedProps> = ({
  name,
  no3d,
  imageUrl,
  color,
  price,
  description,
  Component,
  isSelected,
  onSelect,
  onUnselect,
  alwaysShowSelected,
  selectedNames,
  parentIsSelected,
  carouselImages,
  stopPropagation,
  disabled,
  showComponent: controlledShowComponent,
  onShowComponentToggle,
  scrollOnToggle = true,
  showCheckbox = false,
  deselectByCheckboxOnly = false,
}) => {
  const [isTabOpen, setIsTabOpen] = useState(false);
  const [showDetails, setShowDetails] = React.useState(false);
  const [internalShowComponent, setInternalShowComponent] =
    React.useState(isSelected);
  const containerRef = useRef<HTMLDivElement>(null);
  const scrollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const showComponent =
    controlledShowComponent !== undefined
      ? controlledShowComponent
      : internalShowComponent;

  // const { setIsCarouselVisible, setCarouselImages } = usePopup();

  // const dispatch = useDispatch();

  const handleIconClick = (event: React.MouseEvent<HTMLElement>) => {
    if (carouselImages) {
      event.stopPropagation();
      // dispatch({ type: SET_CAROUSEL_IMAGES, payload: carouselImages });
      // dispatch({ type: SET_CAROUSEL, payload: true });
    }
  };

  const handleIconClickNopCarousel = (event: React.MouseEvent<HTMLElement>) => {
    if (imageUrl) {
      event.stopPropagation();
      // dispatch({ type: SET_CAROUSEL_IMAGES, payload: [imageUrl] });
      // dispatch({ type: SET_CAROUSEL, payload: true });
    }
  };

  const highlight =
    parentIsSelected !== undefined
      ? parentIsSelected && isSelected
      : isSelected;

  const handleDetailseClick = (
    event: React.MouseEvent<HTMLDivElement, MouseEvent>
  ) => {
    event.stopPropagation(); // Prevent the container click event from being triggered
    setIsTabOpen(!isTabOpen);
  };

  let selectedVisible = isSelected;
  if (alwaysShowSelected) selectedVisible = true;

  useEffect(() => {
    // Update internal state only if not controlled
    if (controlledShowComponent === undefined) {
      setInternalShowComponent(isSelected);
    }
  }, [isSelected, controlledShowComponent]);

  const scrollInsideContainer = (
    target: HTMLElement,
    block: ScrollLogicalPosition = "start"
  ) => {
    const scrollParent = target.closest(".scroll") as HTMLElement | null;

    if (scrollParent) {
      const parentRect = scrollParent.getBoundingClientRect();
      const targetRect = target.getBoundingClientRect();
      const delta =
        block === "center"
          ? targetRect.top -
            parentRect.top -
            scrollParent.clientHeight / 2 +
            targetRect.height / 2
          : targetRect.top - parentRect.top;

      scrollParent.scrollTo({
        top: scrollParent.scrollTop + delta,
        behavior: "smooth",
      });
      return;
    }

    target.scrollIntoView({ behavior: "smooth", block });
  };

  const scrollToState = (nextShow: boolean) => {
    if (!scrollOnToggle) return;
    if (!Component) return;

    const node = containerRef.current;
    if (!node) return;

    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
      scrollTimeoutRef.current = null;
    }

    if (nextShow) {
      scrollTimeoutRef.current = setTimeout(() => {
        scrollInsideContainer(node);
      }, 200);
    } else {
      const tabParent = node.closest(".tab") as HTMLElement | null;
      scrollInsideContainer(tabParent ?? node);
    }
  };

  useEffect(() => {
    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, []);

  const hasCheckbox = !Component || showCheckbox;

  const setNextShowComponent = (nextShow: boolean) => {
    scrollToState(nextShow);
    if (controlledShowComponent !== undefined && onShowComponentToggle) {
      onShowComponentToggle(nextShow);
      return;
    }
    if (controlledShowComponent === undefined) {
      setInternalShowComponent(nextShow);
    }
  };

  const handleClick = (event: any) => {
    if (stopPropagation) event.stopPropagation();
    if (disabled) return;

    const nextShow = !showComponent;
    scrollToState(nextShow);
    const preventDeselect = deselectByCheckboxOnly && isSelected;

    // If controlled showComponent is provided, toggle visibility without changing selection
    if (controlledShowComponent !== undefined && onShowComponentToggle) {
      onShowComponentToggle(nextShow);

      if (isSelected && !showComponent) return;
      if (preventDeselect) return;
    }

    // Default behavior: toggle both selection and visibility
    if (isSelected) {
      if (preventDeselect) {
        if (controlledShowComponent === undefined) {
          setInternalShowComponent(nextShow);
        }
        return;
      }
      setInternalShowComponent(false);
      onUnselect();
    } else {
      setInternalShowComponent(true);
      onSelect();
    }
  };
  const ArrowIcon: React.FC = () => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
    >
      <path
        d="M16 12L10 6L8.59 7.41L13.17 12L8.59 16.59L10 18L16 12Z"
        fill="currentColor"
      />
    </svg>
  );

  if (!color && !imageUrl) color = "var(--color-primary-light)";

  const toEuroRound = (price: number) => {
    return `€ ${price}`;
  };

  return (
    <div
      ref={containerRef}
      className={`common-option-container ${
        highlight && (!showComponent || !Component) ? "active_bg" : ""
      } ${highlight && !showComponent ? "active" : ""} ${
        disabled ? "disabled" : ""
      } ${!Component || showCheckbox ? "has-checkbox" : ""}`}
    >
      <div className="common-option" onClick={handleClick}>
        {(!Component || showCheckbox) && !disabled && (
          <div className="checkbox-left" onClick={(e) => e.stopPropagation()}>
            <Checkbox
              checked={isSelected}
              onChange={(checked) => {
                if (Component) {
                  setNextShowComponent(checked);
                }
                if (checked) {
                  onSelect();
                } else {
                  onUnselect();
                }
              }}
            />
          </div>
        )}
        <div
          className={`common-option-thumb${
            (imageUrl && !imageUrl.includes("empty")) || carouselImages
              ? " cursor"
              : ""
          }`}
          style={{ backgroundColor: color }}
          onClick={(event) => {
            if (disabled) return;
            if (carouselImages) {
              event.stopPropagation();

              // setCarouselImages(carouselImages);
              // setIsCarouselVisible(true);
              handleIconClick(event);
            } else if (imageUrl && !imageUrl.includes("empty")) {
              event.stopPropagation();

              // setCarouselImages([imageUrl]);
              // setIsCarouselVisible(true);
              handleIconClickNopCarousel(event);
            }
          }}
        >
          {imageUrl ? <img src={imageUrl} alt={imageUrl} /> : <></>}
        </div>
        <div className="details_container">
          <div className="details_text">
            <div className={`lable  ${highlight ? "active" : ""}`}>
              {name}
              {no3d ? <span className="no3d-text"> (niet in 3D)</span> : null}
            </div>

            <div className="details_buttons_component">
              {price ? (
                <div className="price">
                  {price > 0 ? toEuroRound(price) : ""}
                </div>
              ) : (
                <></>
              )}

              {Component && !disabled ? (
                <div
                  className={`details_icon ${showComponent ? "open" : ""}`}
                  onClick={(event) => {
                    event.stopPropagation();

                    if (
                      controlledShowComponent !== undefined &&
                      onShowComponentToggle
                    ) {
                      const nextShow = !showComponent;
                      scrollToState(nextShow);
                      onShowComponentToggle(nextShow);
                    } else {
                      const nextShow = !showComponent;
                      scrollToState(nextShow);
                      setInternalShowComponent(nextShow);
                    }
                  }}
                >
                  <ArrowIcon />
                </div>
              ) : null}
            </div>
          </div>
          <div className="details_childs">
            <div className="details_childs_content">
              <span>{selectedVisible ? selectedNames : null}</span>
            </div>
            <div className="details_buttons">
              {description && !disabled && (
                <div
                  className={`details ${showDetails ? "open" : ""}`}
                  onClick={(event) => {
                    setShowDetails(!showDetails);
                    event.stopPropagation();
                  }}
                >
                  <span>Details</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      {description && showDetails && (
        <div className="description">{description}</div>
      )}
      {Component && showComponent && (
        <div className="value">
          <div className="component">{<Component />}</div>
        </div>
      )}
    </div>
  );
};

export default SelectUnselectExtended;
